import {Inject, Injectable} from '@angular/core';
import {HttpClient, HttpContext, HttpHeaders} from "@angular/common/http";
import {Observable} from "rxjs";
import {CompositionDto} from "../models/composition-dto";
import {ComponentNl} from "../models/component-nl";
import {ServerEndpoints} from "../server.endpoints";
import {CompositionNl} from "../models/composition-nl";
import {Composition_Create} from "../_modules/inventory/_models/composition-create";
import {DEDUPE_KEY} from "../x-interceptors/duplicate-request.interceptor";

@Injectable({
  providedIn: 'root'
})
export class CompositionHttpService {
  private _baseUrl: string;

  constructor(private http: HttpClient, @Inject('BASE_URL') baseUrl: string, @Inject('BASE_HEADERS') baseHeaders: HttpHeaders) {
    this._baseUrl = baseUrl;

  }

  list(item: string = "", tag: string = "", notype: boolean = false, excludeSpecial: boolean = false): Observable<CompositionDto[]> {
    return this.http.get<CompositionDto[]>(this._baseUrl + ServerEndpoints.Inventory_Composition_List + "?item=" + item + "&tag=" + tag + "&notype=" + notype + "&excludeSpecial=" + excludeSpecial);
  }

  listByIds(ids: string[]): Observable<CompositionNl[]> {
    return this.http.post<CompositionNl[]>(this._baseUrl + ServerEndpoints.Inventory_Composition_List, ids);
  }

  add(name: string, price: number, components: ComponentNl[], tags: string[], special: boolean = false) {
    return this.http.post<CompositionDto>(this._baseUrl + ServerEndpoints.Inventory_Composition_Create, {
      name: name,
      price: price,
      tags: tags,
      components: components,
      special: special
    })
  }

  delete(id: string) {
    return this.http.delete(this._baseUrl + ServerEndpoints.Inventory_Composition_Delete + "?id=" + id);
  }

  toggle(composition: string, tag: string): Observable<CompositionDto> {
    return this.http.put<CompositionDto>(this._baseUrl + ServerEndpoints.Inventory_Composition_ToggleTag + "?composition=" + composition + "&tag=" + tag, {});
  }

  changePrice(composition: string, price: number) {
    return this.http.put<CompositionDto>(this._baseUrl + ServerEndpoints.Inventory_Composition_PriceChange, {
      id: composition,
      price: price
    })
  }

  changeName(composition: string, name: string) {
    return this.http.put<CompositionDto>(this._baseUrl + ServerEndpoints.Inventory_Composition_ChangeName, {
      Id: composition,
      Name: name
    })
  }

  get(id: string, all: boolean = false): Observable<CompositionDto> {
    return this.http.get<CompositionDto>(this._baseUrl + ServerEndpoints.Inventory_Composition_Get + "?id=" + id + "&all=" + String(all));
  }

  download() {
    return this.http.get(this._baseUrl + ServerEndpoints.Inventory_Composition_Download, {
      reportProgress: true,
      observe: 'events',
      responseType: 'blob'
    });
  }

  setOrder(id: string, order: number): Observable<CompositionDto> {
    return this.http.put<CompositionDto>(this._baseUrl + ServerEndpoints.Inventory_Composition_SetOrder + "?id=" + id + "&order=" + order, {});
  }

  changePublicProperty(id: string, field: string, value: string | undefined): Observable<CompositionDto> {
    return this.http.put<CompositionDto>(this._baseUrl + ServerEndpoints.Inventory_Composition_ChangePublicProperty + "?id=" + id, {
      name: field,
      value: value
    })
  }

  changeComponents(compositionId: string, components: ComponentNl[]): Observable<CompositionDto> {
    return this.http.put<CompositionDto>(this._baseUrl + ServerEndpoints.Inventory_Composition_ComponentChange, {
      compositionId: compositionId,
      components: components
    })
  }

  upsert(composition: Composition_Create): Observable<CompositionDto> {
    // Dedupe on the composition's stable identity, not the serialized body: a double-click on save must collapse
    // to one upsert even though the body carries fields (component linkers, timestamps) that can differ between
    // the two clicks. Editing keys on the id; a fresh create (empty id) keys on the name — the create's identity.
    const context = new HttpContext().set(DEDUPE_KEY, `composition-upsert:${composition.id || composition.name}`);
    return this.http.put<CompositionDto>(`${this._baseUrl}${ServerEndpoints.Inventory_Composition_Upsert}`, composition, {context});
  }
}
